// pages/chart/modular/panelRegistry.ts — panel type → definition table (design §6.1)
//
// One entry per PanelType. A definition is UI-shape-only: the wrapper component, the
// seed config (from panelDefaults.ts), grid sizes, and behavior flags. Access tiers
// are a separate concern (PANEL_ACCESS_GATES, §9.1) so this stays shape-only.
//
// PHASE 1: only 'price-chart' is wired to its real wrapper (PriceChartPanel). Every
// other type is registered with its real metadata (label/size/flags) but renders the
// UnknownPanel fallback card until its wrapper lands in a later phase — so presets
// still lay out cleanly and addPanel/setPanelType size correctly. 'unknown' is the
// forward-compat sentinel for a persisted layout that references a removed type.

import { lazy, type ComponentType } from 'react';
import type { PanelConfigByType, PanelType } from './dashboardTypes';
import { PANEL_DEFAULT_CONFIG } from './panelDefaults';
import { UnknownPanel } from './UnknownPanel';

// Panel wrappers are loaded lazily. This registry is reachable from the EAGER
// graph (dashboardStore → dashboardActions → here), so a static import of these
// wrappers would drag the entire chart-panel tree — overlays, lightweight-charts
// (~160 KB), indicator math — into the entry chunk that every page (incl /login)
// downloads first. The registry only needs panel METADATA eagerly; the React
// component is fetched on first render via React.lazy. PanelFrame renders <Comp>
// inside a <Suspense> boundary. UnknownPanel stays static (tiny, no chart deps,
// the forward-compat fallback that must resolve synchronously).
const PriceChartPanel = lazy(() => import('../panels/PriceChartPanel'));
const OrderBookPanel = lazy(() => import('../panels/OrderBookPanel'));
const DepthChartPanel = lazy(() => import('../panels/DepthChartPanel'));
const MarketTradesPanelWrapper = lazy(() => import('../panels/MarketTradesPanelWrapper'));
const WatchlistPanelWrapper = lazy(() => import('../panels/WatchlistPanelWrapper'));
const IndicatorPanePanel = lazy(() => import('../panels/IndicatorPanePanel'));
const TickerStatsPanel = lazy(() => import('../panels/TickerStatsPanel'));
const TradeTicketPanel = lazy(() => import('../panels/TradeTicketPanel'));
const PositionsPlPanel = lazy(() => import('../panels/PositionsPlPanel'));
const MarketOverviewHeatmapPanel = lazy(() => import('../panels/MarketOverviewHeatmapPanel'));
const ForecastPanelWrapper = lazy(() => import('../panels/ForecastPanelWrapper'));
const SentimentPanelWrapper = lazy(() => import('../panels/SentimentPanelWrapper'));
const NewsFeedStubPanel = lazy(() => import('../panels/NewsFeedStubPanel'));

export interface PanelDefinition {
  type: PanelType;
  label: string;
  category: 'chart' | 'market-data' | 'trading' | 'analytics';
  /** Thin wrapper from panels/*; receives the panel id and resolves its own data. */
  component: ComponentType<{ panelId: string }>;
  /** Discriminated-union seed config for a freshly-minted panel of this type. */
  defaultConfig: PanelConfigByType[PanelType];
  defaultSize: { w: number; h: number };
  minSize: { minW: number; minH: number };
  /** Render the requiresSymbol empty state when no symbol resolves (PanelFrame). */
  requiresSymbol: boolean;
  canDuplicate: boolean;
  /** M5: availability gate — true until the data source/wrapper is built. */
  experimental?: boolean;
}

/** Forward-compat fallback for a persisted layout referencing a removed type. */
export const UNKNOWN_PANEL: PanelDefinition = {
  type: 'unknown',
  label: 'Unavailable',
  category: 'analytics',
  component: UnknownPanel,
  defaultConfig: PANEL_DEFAULT_CONFIG.unknown,
  defaultSize: { w: 4, h: 6 },
  minSize: { minW: 2, minH: 3 },
  requiresSymbol: false,
  canDuplicate: false,
};

// Catalog metadata (§6.2). Wrappers not yet built render UnknownPanel but keep their
// real sizes/flags so the grid + addPanel behave correctly before later phases.
export const PANEL_REGISTRY: Record<PanelType, PanelDefinition> = {
  'price-chart': {
    type: 'price-chart',
    label: 'Price Chart',
    category: 'chart',
    component: PriceChartPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['price-chart'],
    defaultSize: { w: 8, h: 12 },
    minSize: { minW: 4, minH: 6 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'indicator-pane': {
    type: 'indicator-pane',
    label: 'Indicator Sub-Pane',
    category: 'chart',
    component: IndicatorPanePanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['indicator-pane'],
    defaultSize: { w: 4, h: 4 },
    minSize: { minW: 2, minH: 3 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'order-book': {
    type: 'order-book',
    label: 'Order Book',
    category: 'market-data',
    component: OrderBookPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['order-book'],
    defaultSize: { w: 2, h: 9 },
    minSize: { minW: 2, minH: 6 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'depth-chart': {
    type: 'depth-chart',
    label: 'Market Depth',
    category: 'chart',
    component: DepthChartPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['depth-chart'],
    defaultSize: { w: 3, h: 7 },
    minSize: { minW: 2, minH: 5 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'market-trades': {
    type: 'market-trades',
    label: 'Market Trades',
    category: 'market-data',
    component: MarketTradesPanelWrapper,
    defaultConfig: PANEL_DEFAULT_CONFIG['market-trades'],
    defaultSize: { w: 2, h: 14 },
    minSize: { minW: 2, minH: 4 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'watchlist-panel': {
    type: 'watchlist-panel',
    label: 'Watchlist',
    category: 'market-data',
    component: WatchlistPanelWrapper,
    defaultConfig: PANEL_DEFAULT_CONFIG['watchlist-panel'],
    defaultSize: { w: 2, h: 24 },
    minSize: { minW: 2, minH: 8 },
    requiresSymbol: false,
    canDuplicate: false,
  },
  'trade-ticket': {
    type: 'trade-ticket',
    label: 'Trade Ticket',
    category: 'trading',
    component: TradeTicketPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['trade-ticket'],
    defaultSize: { w: 4, h: 8 },
    minSize: { minW: 3, minH: 6 },
    requiresSymbol: true,
    canDuplicate: false,
  },
  'ticker-stats': {
    type: 'ticker-stats',
    label: 'Ticker Stats',
    category: 'market-data',
    component: TickerStatsPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['ticker-stats'],
    defaultSize: { w: 12, h: 2 },
    minSize: { minW: 2, minH: 2 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'market-overview-heatmap': {
    type: 'market-overview-heatmap',
    label: 'Market Overview Heatmap',
    category: 'market-data',
    component: MarketOverviewHeatmapPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['market-overview-heatmap'],
    defaultSize: { w: 6, h: 7 },
    minSize: { minW: 3, minH: 2 },
    requiresSymbol: false,
    canDuplicate: false,
  },
  'forecast-panel': {
    type: 'forecast-panel',
    label: 'Forecast Summary',
    category: 'chart',
    component: ForecastPanelWrapper,
    defaultConfig: PANEL_DEFAULT_CONFIG['forecast-panel'],
    defaultSize: { w: 4, h: 7 },
    minSize: { minW: 3, minH: 5 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'sentiment-panel': {
    type: 'sentiment-panel',
    label: 'Sentiment',
    category: 'analytics',
    component: SentimentPanelWrapper,
    defaultConfig: PANEL_DEFAULT_CONFIG['sentiment-panel'],
    defaultSize: { w: 4, h: 3 },
    minSize: { minW: 3, minH: 2 },
    requiresSymbol: true,
    canDuplicate: true,
  },
  'positions-pl': {
    type: 'positions-pl',
    label: 'Positions & P/L',
    category: 'analytics',
    component: PositionsPlPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['positions-pl'],
    defaultSize: { w: 6, h: 5 },
    minSize: { minW: 3, minH: 3 },
    requiresSymbol: false,
    canDuplicate: false,
  },
  'news-feed': {
    type: 'news-feed',
    label: 'News Feed',
    category: 'market-data',
    component: NewsFeedStubPanel,
    defaultConfig: PANEL_DEFAULT_CONFIG['news-feed'],
    defaultSize: { w: 6, h: 8 },
    minSize: { minW: 3, minH: 4 },
    requiresSymbol: true,
    canDuplicate: false,
    // Honest stub: renders a "data source TBD" shell (no backend feed exists yet).
    // Kept experimental so the catalog flags it as not-yet-real (design v2.1 §10 #1).
    experimental: true,
  },
  unknown: UNKNOWN_PANEL,
};

/** Lookup with forward-compat fallback for an unregistered/removed type. */
export const getPanelDef = (t: PanelType): PanelDefinition | undefined =>
  PANEL_REGISTRY[t];
